import React from 'react';
import { Stack, CircularProgress, Typography } from '@mui/material';
import { useSummaryFetch, WidgetLoadError } from './useSummaryFetch';
import { UserType } from '../../../interfaces/userType.enum';
import { DashboardService } from '../../../services/dashboard.service';
import { AccountingSummary, MonthlyIncomeExpense, ArAgingBucket, AccountAmount, TopReceivable } from '../../../interfaces/dashboard.interface';
import { WidgetDef, WidgetProps } from './types';
import StatTile from '../charts/StatTile';
import Columns from '../charts/Columns';
import HBars from '../charts/HBars';
import { fmtMoney, CURRENCY_SYMBOL } from './format';

const BUSINESS_ROLES = [UserType.InstitutionAdmin, UserType.InstitutionManager, UserType.Accountant];

const loadAccounting = (institutionId: string): Promise<AccountingSummary> =>
    DashboardService.getAccountingSummary(institutionId);

const AccountingWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadAccounting, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // A real zero (e.g. every invoice paid, every bill settled, cash net to 0.00) is a genuine
    // figure, not an absence of data — render it as a zero rather than collapsing it into an
    // "empty state" claim that isn't true.
    return (
        <Stack direction="row" spacing={2} useFlexGap sx={{ flexWrap: 'wrap' }}>
            <StatTile label="Receivable" unit={CURRENCY_SYMBOL} value={fmtMoney(d.arOutstanding)} tone={d.arOutstanding > 0 ? 'warning' : 'default'} />
            <StatTile label="Payable" unit={CURRENCY_SYMBOL} value={fmtMoney(d.apOutstanding)} tone={d.apOutstanding > 0 ? 'warning' : 'default'} />
            {/* Cash is only "good" when positive: a zero is neutral and an overdrawn/net-credit
                balance must never render success-green. */}
            <StatTile
                label="Cash & bank"
                unit={CURRENCY_SYMBOL}
                value={fmtMoney(d.cashBalance)}
                tone={d.cashBalance > 0 ? 'good' : d.cashBalance < 0 ? 'critical' : 'default'}
            />
        </Stack>
    );
};

const loadAccountingTrend = (institutionId: string): Promise<MonthlyIncomeExpense[]> =>
    DashboardService.getAccountingTrend(institutionId);

const AccountingTrendWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadAccountingTrend, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No posted journal activity yet — post journal entries to see income and expense here.</Typography>;
    }
    // A month where nothing was posted is a genuine zero: it stays on the axis as a zero column.
    // Both charts share one scale (the larger of the two series' maxima) so income and expense
    // are visually comparable — a real-zero month (both series flat 0) falls back to the default
    // auto-scale via Columns' own Math.max(1, ...) guard.
    const sharedMax = Math.max(...d.map(p => p.income), ...d.map(p => p.expense));
    return (
        <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">Income</Typography>
            <Columns data={d.map(p => ({ label: p.month, value: p.income }))} yFmt={fmtMoney} colorIndex={0} height={140} max={sharedMax} />
            <Typography variant="caption" color="text.secondary">Expense</Typography>
            <Columns data={d.map(p => ({ label: p.month, value: p.expense }))} yFmt={fmtMoney} colorIndex={1} height={140} max={sharedMax} />
        </Stack>
    );
};

const loadArAging = (institutionId: string): Promise<ArAgingBucket[]> =>
    DashboardService.getAccountingArAging(institutionId);

const AccountingArAgingWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadArAging, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    // The backend zero-fills all five buckets while any invoice is open, so an empty array
    // really does mean "no open invoices" — a zero-filled bucket set renders as zero bars.
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No open invoices — nothing is aging.</Typography>;
    }
    return (
        <Stack spacing={0.5}>
            <HBars
                data={d.map(b => ({
                    label: b.bucket,
                    value: b.amount,
                    tooltip: [
                        { label: 'Outstanding', value: fmtMoney(b.amount) },
                        { label: 'Invoices', value: String(b.invoiceCount) },
                    ],
                }))}
                vFmt={fmtMoney}
            />
            <Typography variant="caption" color="text.secondary">
                Invoices without a due date age from issue + 30 days
            </Typography>
        </Stack>
    );
};

const loadExpenseByAccount = (institutionId: string): Promise<AccountAmount[]> =>
    DashboardService.getAccountingExpenseByAccount(institutionId);

const AccountingExpenseByAccountWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadExpenseByAccount, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No expense postings yet — post journal entries to see spend by account.</Typography>;
    }
    return (
        <HBars
            data={d.map(a => ({
                label: a.accountName,
                value: a.amount,
                tooltip: [{ label: 'Spend', value: fmtMoney(a.amount) }],
            }))}
            vFmt={fmtMoney}
        />
    );
};

const loadTopReceivables = (institutionId: string): Promise<TopReceivable[]> =>
    DashboardService.getAccountingTopReceivables(institutionId);

const AccountingTopReceivablesWidget: React.FC<WidgetProps> = ({ institutionId }) => {
    const { data: d, failed } = useSummaryFetch(loadTopReceivables, institutionId);
    if (failed) return <WidgetLoadError />;
    if (!d) return <CircularProgress size={20} />;
    if (d.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No outstanding invoices — every customer is settled.</Typography>;
    }
    return (
        <HBars
            data={d.map(r => ({
                label: r.customerName,
                value: r.outstanding,
                tooltip: [
                    { label: 'Outstanding', value: fmtMoney(r.outstanding) },
                    { label: 'Invoices', value: String(r.invoiceCount) },
                ],
            }))}
            vFmt={fmtMoney}
        />
    );
};

export const accountingWidgets: WidgetDef[] = [
    { id: 'accounting.summary', module: 'accounting', title: 'Accounting — AR, AP & cash', roles: BUSINESS_ROLES, size: 'md', component: AccountingWidget },
    { id: 'accounting.trend', module: 'accounting', title: 'Accounting — income vs expense', roles: BUSINESS_ROLES, size: 'lg', component: AccountingTrendWidget },
    { id: 'accounting.arAging', module: 'accounting', title: 'Accounting — AR aging', roles: BUSINESS_ROLES, size: 'md', component: AccountingArAgingWidget },
    { id: 'accounting.expenseByAccount', module: 'accounting', title: 'Accounting — expense by account', roles: BUSINESS_ROLES, size: 'md', component: AccountingExpenseByAccountWidget },
    { id: 'accounting.topReceivables', module: 'accounting', title: 'Accounting — top receivables', roles: BUSINESS_ROLES, size: 'md', component: AccountingTopReceivablesWidget },
];
